import React, { memo, useCallback, useEffect, useState } from "react";
import { IconContext } from "react-icons";
import * as MdIcons from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useProfile } from "../../contexts/ProfileContext.tsx";
import Icon, { IconNames } from "../../core/components/Icon.tsx";
import Separator from "../../core/components/Separator.tsx";
import useOutsideClick from "../../hooks/useOutsideClick.ts";
import iconeUsuario from "../../images/iconeUsuario.png";
import logoNavbar from "../../images/logoNavbar.png";
import ModalAlerta from "../ModalAlerta/ModalAlerta.tsx";
import ModalConfirmacao2 from "../ModalConfirmacao2/ModalConfirmacao2.tsx";
import ModalNotificacao from "../ModalNotificacao/ModalNotificacao.tsx";
import "../NavbarV3/NavbarV3.css";
import {
	NavbarItems,
	NavbarRightItems,
} from "./Dropdowns/constants/Dropdown.constants.tsx";
import DropdownPesquisa from "./Dropdowns/DropdownPesquisa.tsx";
import DropdownUser from "./Dropdowns/DropdownUser.tsx";
import { NavbarRightItemsEnum } from "./Dropdowns/interfaces/Dropdown.enum.ts";

interface NavbarProps {
	title: string;
	notificoes?: string;
}

interface NavbarItemProps {
	title: string;
	children: React.ReactNode;
}

interface NavbarRightItemProps {
	icon?: IconNames;
	img?: string;
	children: React.ReactNode;
}

const NavbarItem = ({ title, children }: NavbarItemProps) => {
	const [isOpenDropdown, toggleDropdown, , refDropdown] = useOutsideClick();
	return (
		<div ref={refDropdown} className="navbar-title" onClick={toggleDropdown}>
			<p className="title-menu">{title}</p>
			{isOpenDropdown ? (
				<>
					<MdIcons.MdOutlineKeyboardArrowDown color="#FFF" />
					{children}
				</>
			) : (
				<MdIcons.MdOutlineKeyboardArrowUp color="#FFF" />
			)}
		</div>
	);
};

const NavbarRightItem = ({ icon, img, children }: NavbarRightItemProps) => {
	const [isOpenDropdown, toggleDropdown, , refDropdown] = useOutsideClick();

	if (!icon && !img) return;

	const className = img ? "image-perfil" : "navbar-title";
	return (
		<div ref={refDropdown} className={className} onClick={toggleDropdown}>
			{icon && !img && <Icon name={icon} size="xl" theme="light" />}
			{img && <img src={img} alt="icone" />}
			{isOpenDropdown && children}
		</div>
	);
};

const PageTitle = ({ title }: { title: string }) => {
	return (
		<div className="page-title">
			<p className="title">{title}</p>
		</div>
	);
};

const NavbarItemList = ({ isMobile }: { isMobile: boolean }) => {
	const navigate = useNavigate();

	const filteredItems = isMobile
		? Object.keys(NavbarItems).filter(
			(key) => NavbarItems[key]?.titleNavbar === "Viagem e Pedidos" || NavbarItems[key]?.titleNavbar === "Dashboard"
		)
		: Object.keys(NavbarItems);

	return (
		<div className="navbar-icons">
			<div className="navbar-icons-container">
				<div
					onClick={() => {
						navigate("/");
					}}
					className="navbar-inicio"
				>
					<img
						style={{ cursor: "pointer" }}
						src={logoNavbar}
						alt="logoNavbar"
						className="iconeMenu"
					/>
					<p className="title-menu-inicio">Início</p>
				</div>
				{!isMobile && (
					<>
						<Separator direction="horizontal" size="sm" />
						<DropdownPesquisa />
					</>
				)}
			</div>

			<Separator direction="horizontal" size="sm" />

			{filteredItems.map((key) => {
				const item = NavbarItems[key];
				return (
					<NavbarItem
						key={key}
						title={item.titleNavbar || ""}
						children={item.dropdown()}
					/>
				);
			})}

			<Separator direction="horizontal" size="sm" />
		</div>
	);
};

function useIsMobile(breakpoint = 768) {
	const query = `(max-width: ${breakpoint}px)`;

	const getMatches = () => window.matchMedia(query).matches;
	const [isMobile, setIsMobile] = useState(getMatches);

	useEffect(() => {
		const mql = window.matchMedia(query);

		const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);

		setIsMobile(mql.matches);

		if (mql.addEventListener) mql.addEventListener("change", onChange);
		else mql.addListener(onChange);

		return () => {
			if (mql.removeEventListener) mql.removeEventListener("change", onChange);
			else mql.removeListener(onChange);
		};
	}, [query]);

	return isMobile;
}

function NavbarV3({ notificoes, title }: NavbarProps) {
	const { logout, coresEmpresa, profile } = useProfile();

	const navigate = useNavigate();

	const [isOpenLogoutModal, toggleLogoutModal, closeLogoutModal, ,] =
		useOutsideClick();
	const [
		isOpenNotificacao,
		toggleNotificacao,
		closeNotificacao,
		refNotificacao,
	] = useOutsideClick();
	const [isOpenExpiracao, , closeExpiracao] = useOutsideClick();
	const [
		isOpenDropdownUser,
		toggleUserDropdownOpen,
		closeUserDropdownOpen,
		refUser,
	] = useOutsideClick();

	const handleClose = useCallback(() => {
		closeLogoutModal();
	}, [closeLogoutModal]);

	const handleCloseExpiracao = useCallback(() => {
		closeExpiracao();
	}, [closeExpiracao]);

	const handleCloseNotificacao = useCallback(() => {
		closeNotificacao();
	}, [closeNotificacao]);

	const handleLogoutClick = useCallback(() => {
		closeUserDropdownOpen();
		toggleLogoutModal();
	}, [closeUserDropdownOpen, toggleLogoutModal]);

	const isMobile = useIsMobile(768);

	return (
		<>
			<IconContext.Provider value={{ color: "#fff" }}>
				<div
					className="navbar"
					style={{ backgroundColor: coresEmpresa.headerFooter }}
				>
					<NavbarItemList isMobile={isMobile} />

					<PageTitle title={title} />
					<div className="right-icons">
						<div
							onClick={() => {
								navigate("/ajuda");
							}}
							className="navbar-title"
						>
							<Icon name="MessageCircleQuestion" size="xl" theme="light" />
						</div>
						<div
							ref={refNotificacao}
							onClick={toggleNotificacao}
							className="navbar-title"
						>
							<Icon name="Bell" size="xl" theme="light" />
						</div>
						<NavbarRightItem
							icon={NavbarRightItems[NavbarRightItemsEnum.CONFIG].icon}
							children={NavbarRightItems[
								NavbarRightItemsEnum.CONFIG
							].dropdown()}
						/>
						<div
							ref={refUser}
							onClick={toggleUserDropdownOpen}
							className="image-perfil"
						>
							{profile?.profileImage && (
								<img
									src={
										profile?.profileImage && profile?.profileImage.tipo
											? `data:${profile?.profileImage.tipo};base64,${profile?.profileImage.dado}`
											: iconeUsuario
									}
									alt="Uploaded preview"
								/>
							)}
							{!profile?.profileImage && (
								<Icon name="User" size="xl" theme="light" />
							)}
						</div>
						{isOpenDropdownUser && (
							<DropdownUser
								ref={refUser}
								handleLogoutClick={handleLogoutClick}
							/>
						)}
					</div>
				</div>
			</IconContext.Provider>
			<ModalConfirmacao2
				titulo="Sair"
				texto="Deseja Sair do Sistema?"
				tituloBotao="Cancelar"
				textoBotao="Sair"
				open={isOpenLogoutModal}
				onClick={logout}
				onClose={handleClose}
			/>
			<ModalNotificacao
				titulo={notificoes}
				tituloConfirmar="OK"
				open={isOpenNotificacao}
				onClose={handleCloseNotificacao}
			/>
			<ModalAlerta
				open={isOpenExpiracao}
				onClose={handleCloseExpiracao}
				titulo="Expiração"
			/>
		</>
	);
}

export default memo(NavbarV3);
